import { getColumns } from './EndpointFilterBuilder.Grid';
import BespokeConditionBuilder from '../../Functions/Bespoke/BespokeConditionBuilder';
import ConditionBuilder from '../../Functions/Condition/ConditionBuilder';
import conditionDisplayHelper from '../../../infrastructure/helpers/functions/condition/conditionDisplayHelper';
import EndpointFilter from '../../../types/report/EndpointFilter';
import ErrorList from '../../Common/ErrorList/ErrorList';
import React from 'react';
import reportConfigNavigation from '../../../infrastructure/helpers/report/navigation/reportConfigNavigation';
import ReportLogicList from '../Common/ReportLogicList/ReportLogicList';
import ReportLogicLocation from '../../../types/functions/Location/ReportLogicLocation';
import StatementBuilderProps from '../../Functions/Statement/StatementBuilderProps';
import Condition from '../../../types/functions/Condition/Condition';
import assignmentHelper from '../../../infrastructure/helpers/functions/assignment/assignmentHelper';
import reorderHelper from '../../../infrastructure/helpers/common/reorderHelper';
import ComponentType from '../../../types/report/ComponentType';
import DragAndDropItem from '../../../types/report/DragAndDropItem';
import reportComponentBuildersHelper from '../Common/ReportComponentsHelper';
import copyArray from '../../../infrastructure/helpers/common/copyArray';
import { endpointFilterType } from '../../../infrastructure/constants/report/endpointFilterType';

class EndpointFilterBuilder extends React.Component<StatementBuilderProps<EndpointFilter>> {
    constructor(props: StatementBuilderProps<EndpointFilter>) {
        super(props);

        this.onNameChange = this.onNameChange.bind(this);
        this.onBusinessDescriptionChange = this.onBusinessDescriptionChange.bind(this);
        this.onBespokeToggleClick = this.onBespokeToggleClick.bind(this);
        this.getConditionContent = this.getConditionContent.bind(this);
        this.reorder = this.reorder.bind(this);
        this.onDropBetweenFilters = this.onDropBetweenFilters.bind(this);
    }

    onNameChange(name: string, number: number): void {

        this.props.onStatementChange({ number: number, name: name } as EndpointFilter);
    }

    onBusinessDescriptionChange(businessDescription: string, number: number): void {

        this.props.onStatementChange({ number: number, businessDescription: businessDescription } as EndpointFilter);
    }

    onBespokeToggleClick(isBespoke: boolean, filter: EndpointFilter): void {

        let changes = { number: filter.number, isBespoke: isBespoke } as EndpointFilter;

        if (isBespoke) {
            changes.bespokeCondition = conditionDisplayHelper.buildConditions(filter.condition, [], [], [], [], this.props.collections.reportFields, null as any, this.props.reportConfig.type);
        }

        this.props.onStatementChange(changes);
    }

    onReorderConditions(condition: Condition, filter: EndpointFilter) {

        filter.condition = condition;
        this.props.onStatementChange(filter);
    }

    async onClearClick(condition: Condition, filter: EndpointFilter): Promise<void> {

        let newFilter = assignmentHelper.clearOperandsInStatementByConditionNumber(condition.number, filter) as EndpointFilter;

        this.props.onStatementChange(newFilter);
    }

    clearAllErrors() {
        this.props.reportConfig.endpointFilters.forEach((filter) => {
            filter.errors = [];
        });
    }

    async reorder(dragNumber: number, dropNumber: number): Promise<void> {

        let dragIndex = this.props.statements.findIndex(x => x.number === dragNumber);
        let dropIndex = this.props.statements.findIndex(x => x.number === dropNumber);

        let draggedFilter = this.props.statements.find(v => v.number === dragNumber) as EndpointFilter;
        let dropFilter = this.props.statements.find(v => v.number === dropNumber) as EndpointFilter;

        this.clearAllErrors();

        let result = reportComponentBuildersHelper.validateDragAndDrop(draggedFilter as DragAndDropItem, dropFilter as DragAndDropItem, ComponentType.EndpointFilter);

        if (result.error) {
            const filterReferenced = this.props.statements.find(s => s.number === result.number) || {} as EndpointFilter;

            result.error = `${result.error} The referenced filter is "${filterReferenced.name}".`;

            const number = result.isDrag ? draggedFilter.number : dropFilter.number;

            const statement = this.props.statements.find(s => s.number === number) || {} as EndpointFilter;

            statement.errors = [result.error];

            this.props.onStatementChange(statement);

            return;
        }
        else {

            reorderHelper.reorderWithNumbersChange(this.props.statements, dragIndex, dropIndex);

            let filterType = this.props.statements?.[0]?.type;

            let otherFilters = copyArray(this.props.reportConfig.endpointFilters).filter(x => x.type !== filterType);

            this.props.onDragAndDrop(this.props.statements.concat(otherFilters));
        }
    }

    onDropBetweenFilters(dragNumber: number, dropNumber: number) {

        let numbersMap = reorderHelper.prepareNewNumbersMapOnDragBetween(dragNumber, dropNumber);

        this.props.onDragStatementBetween && this.props.onDragStatementBetween(numbersMap);
    }

    getConditionContent(filter: EndpointFilter): JSX.Element {

        if (filter.isBespoke) {
            return (
                <BespokeConditionBuilder
                    keyword={this.props.details.keyword}
                    keyword2={this.props.details.keyword2}
                    statement={filter}
                    onChange={this.props.onStatementChange}
                    isReadOnly={this.props.isReadOnly} />
            );
        }

        return (
            <>
                <ConditionBuilder
                    reportConfigType={this.props.reportConfig.type}
                    isReadOnly={this.props.isReadOnly}
                    allowCopy={true}
                    keyword={this.props.details.keyword}
                    functions={this.props.reportConfig.userDefinedFunctions}
                    globalFunctions={this.props.collections.globalModuleConfig.userDefinedFunctions}
                    lookups={this.props.reportConfig.lookups}
                    dictionaries={this.props.reportConfig.dictionaries}
                    variables={this.props.reportConfig.variables}
                    dataSources={this.props.collections.dataSources}
                    reportFields={this.props.collections.reportFields}
                    customDataSourceFields={null as any}
                    location={{ statement: this.props.details.statement, statementNumber: filter.number } as ReportLogicLocation}
                    condition={filter.condition}
                    onLogicalOperatorChange={this.props.onLogicalOperatorChange}
                    onParenthesesChange={this.props.onParenthesesChange}
                    onAddConditionClick={this.props.onAddConditionClick}
                    onRemoveConditionClick={this.props.onRemoveConditionClick}
                    onComparisonTypeChange={this.props.onComparisonTypeChange}
                    onOperandClick={this.props.onOperandClick}
                    onCopyClick={this.props.onCopyClick}
                    onPasteClick={this.props.onPasteClick}
                    allowPaste={this.props.allowPaste}
                    onClearClick={async (e, condition) => { this.onClearClick(condition, filter); }}
                    onReorderConditions={(condition) => { this.onReorderConditions(condition, filter); }} />

                <div className="code-block">
                    <span className="code">{this.props.details.keyword2}</span>
                </div>

                <ErrorList errors={filter.errors} className="mt-2" />
            </>
        );
    }

    render(): JSX.Element {

        let filterType = this.props.statements?.[0]?.type === endpointFilterType.regular ? ComponentType.EndpointFilter :
            this.props.statements?.[0]?.type === endpointFilterType.underReport ? ComponentType.UnderReportFilter :
                ComponentType.OverReportFilter;

        return (
            <ReportLogicList
                onDropZoneDrop={this.onDropBetweenFilters}
                isWaiting={this.props.isWaiting}
                items={this.props.statements}
                columns={getColumns(this.onNameChange,
                    this.onBusinessDescriptionChange,
                    this.onBespokeToggleClick,
                    this.props.onRemoveStatementClick,
                    this.reorder,
                    this.props.isReadOnly,
                    filterType)}
                getContent={this.getConditionContent}
                getKey={f => f.number}
                createHtmlId={reportConfigNavigation.buildEndpointFilterId} />
        );
    }
}

export default EndpointFilterBuilder;
